import { useEffect, useRef, useState } from 'react';
import moment from 'moment';
import { i18n } from '@lingui/core';
import { t } from '@lingui/macro';
import _, { flatMap, mapValues } from 'lodash';
import {
    AGENT_2K_TEST_LIMIT,
    AGENT_3K_TEST_LIMIT,
    AGENT_GLOBAL_TEST_LIMIT,
    AGENT_CPU_LOAD,
    AGENT_HARDWARE_2K_MODEL,
    AGENT_HARDWARE_3K_MODEL,
    AGENT_IS_FLEXXON_CARD,
    AGENT_MEMORY_USED_PERCENT,
    AGENT_MODEL_SHORT_FIELD,
    AGENT_SD_AVG_ERASE_COUNT,
    AGENT_SD_LIFE_REMAINING,
    AGENT_SD_MAX_ERASE_COUNT,
    AGENT_TEMPERATURE,
    AGENT_TEST_COUNT_FIELD,
    AGENT_TX_BUDGET_USED,
    AGENT_TX_TRANSFERRED_BYTES,
    AGENT_RX_TRANSFERRED_BYTES,
    AGENT_TX_BUDGET_RESET_MINUTES,
    AGENT_VIRTUAL_2K_MODEL,
    AGENT_VIRTUAL_3K_MODEL,
    AGENT_GLOBAL_MODEL,
    AGENT_VITALS_MEMORY_USED_PERCENT,
    AGENT_VITALS_SD_LIFE_REMAINING,
    AGENT_VITALS_SD_MAX_ERASE_COUNT,
} from './AgentConstants';
import { useDataService } from '../../../services/ApiDataService';
import { useGenericError } from '../../../services/FormErrorService';
import { getSystemTime } from '../../../stores/SystemTimeStore';
import { FORMATTER_TYPES } from '../../../utilities/FormattingUtilities';
import { BuiltInTooltip } from '../../charts/tooltip/types';
import { hasTxBudget } from './AgentHelper';

const TREND_CHART_OPTIONS = {
    [AGENT_MEMORY_USED_PERCENT]: {
        title: 'Memory',
        yTitle: 'Memory',
        units: '%',
        yMin: 0,
        yMax: 100,
        chartLineColor: '#B2912F',
    },
    [AGENT_CPU_LOAD]: {
        title: 'CPU',
        yTitle: 'CPU',
        units: '%',
        yMin: 0,
        yMax: 100,
        chartLineColor: '#5DA5DA',
    },
    [AGENT_TEMPERATURE]: {
        title: 'Temperature',
        yTitle: 'Temp',
        units: 'C',
        yMin: 0,
        yMax: 100,
        chartLineColor: '#B276B2',
    },
    [AGENT_TEST_COUNT_FIELD]: {
        title: 'Service Tests',
        yTitle: 'Tests',
        units: '',
        yMin: 0,
        chartLineColor: '#789D4A',
    },
    [AGENT_SD_LIFE_REMAINING]: {
        title: 'Storage Endurance',
        yTitle: 'Endurance',
        units: '%',
        yMin: 0,
        chartLineColor: '#3A6178',
    },
    [AGENT_SD_MAX_ERASE_COUNT]: {
        title: 'Storage Maximum Erase',
        yTitle: 'Max Erase',
        units: '',
        yMin: 0,
        chartLineColor: '#BF3D56',
    },
    [AGENT_SD_AVG_ERASE_COUNT]: {
        title: 'Storage Average Erase',
        yTitle: 'Avg Erase',
        units: '',
        yMin: 0,
        chartLineColor: '#191919',
    },
    [AGENT_TX_BUDGET_USED]: {
        title: 'Transmit Budget Used',
        yTitle: 'Budget Used',
        units: '%',
        yMin: 0,
        yMax: 100,
        chartLineColor: '#EB8934',
    },
    [AGENT_TX_BUDGET_RESET_MINUTES]: {
        title: 'Transmit Budget Reset',
        yTitle: 'Reset',
        units: 'minutes',
        yMin: 0,
        yMax: 70,
        chartLineColor: '#34EBA8',
    },
    [AGENT_TX_TRANSFERRED_BYTES]: {
        formatter: FORMATTER_TYPES.BYTERATE,
        title: 'Transmit',
        yTitle: 'Transmit (Bytes)',
        units: undefined,
        yMin: 0,
        chartLineColor: '#55BDE0',
    },
    [AGENT_RX_TRANSFERRED_BYTES]: {
        formatter: FORMATTER_TYPES.BYTERATE,
        title: 'Receive',
        yTitle: 'Receive  (Bytes)',
        units: undefined,
        yMin: 0,
        chartLineColor: '#a069db',
    },
};

const DEFAULT_HEALTH_REFRESH_INTERVAL = 30000;
const ONE_MIN_AGGREGATION_VALUE = 60000;

export const TIME_RANGE_8_HOURS_OPTION = 480;
export const TIME_RANGE_OPTIONS = {
    60: i18n._(t`Last 60 Minutes`),
    [TIME_RANGE_8_HOURS_OPTION]: i18n._(t`Last 8 Hours`),
    1440: i18n._(t`Last 24 Hours`),
    10080: i18n._(t`Last 7 Days`),
    43200: i18n._(t`Last 30 Days`),
};

export function buildChartOptions(agent = {}) {
    const baseOptions = _.cloneDeep(TREND_CHART_OPTIONS);
    const agentModelShort = agent[AGENT_MODEL_SHORT_FIELD];

    if (agentModelShort === AGENT_HARDWARE_2K_MODEL || agentModelShort === AGENT_VIRTUAL_2K_MODEL) {
        _.set(baseOptions, `${AGENT_TEST_COUNT_FIELD}.yMax`, AGENT_2K_TEST_LIMIT);
    }

    if (agentModelShort === AGENT_HARDWARE_3K_MODEL || agentModelShort === AGENT_VIRTUAL_3K_MODEL) {
        _.set(baseOptions, `${AGENT_TEST_COUNT_FIELD}.yMax`, AGENT_3K_TEST_LIMIT);
    }

    if (agentModelShort === AGENT_GLOBAL_MODEL) {
        _.set(baseOptions, `${AGENT_TEST_COUNT_FIELD}.yMax`, AGENT_GLOBAL_TEST_LIMIT);
    }
    return baseOptions;
}

export function buildTrendCharts(agent, isHardwareAgent, isGlobalAgent, baseChartOptions) {
    const charts = [];
    const metrics = [];
    const isFlexxonCard = _.get(agent, AGENT_IS_FLEXXON_CARD, false);

    if (isHardwareAgent) {
        // This metric is new in 3.3, so make sure the nPoint is sending it up before we show it
        if (_.get(agent, AGENT_VITALS_MEMORY_USED_PERCENT)) {
            charts.push(buildChartForMetric(AGENT_MEMORY_USED_PERCENT, baseChartOptions, agent));
            metrics.push(AGENT_MEMORY_USED_PERCENT);
        }
        charts.push(buildChartForMetric(AGENT_CPU_LOAD, baseChartOptions, agent));
        charts.push(buildChartForMetric(AGENT_TEMPERATURE, baseChartOptions, agent));
        metrics.push(AGENT_CPU_LOAD);
        metrics.push(AGENT_TEMPERATURE);
    }

    charts.push(buildChartForMetric(AGENT_TEST_COUNT_FIELD, baseChartOptions, agent));
    metrics.push(AGENT_TEST_COUNT_FIELD);

    if (isFlexxonCard || !_.isEmpty(_.get(agent, AGENT_VITALS_SD_LIFE_REMAINING))) {
        charts.push(buildChartForMetric(AGENT_SD_LIFE_REMAINING, baseChartOptions, agent));
        metrics.push(AGENT_SD_LIFE_REMAINING);
    }

    if (isFlexxonCard || _.get(agent, AGENT_VITALS_SD_MAX_ERASE_COUNT)) {
        charts.push(buildChartForMetric(AGENT_SD_MAX_ERASE_COUNT, baseChartOptions, agent));
        metrics.push(AGENT_SD_MAX_ERASE_COUNT);
    }

    if (isGlobalAgent) {
        // These metrics are new in 3.9, older agents will not have them
        if (hasTxBudget(agent)) {
            charts.push(buildChartForMetric(AGENT_TX_BUDGET_USED, baseChartOptions, agent));
            metrics.push(AGENT_TX_BUDGET_USED);
            charts.push(buildChartForMetric(AGENT_TX_BUDGET_RESET_MINUTES, baseChartOptions, agent));
            metrics.push(AGENT_TX_BUDGET_RESET_MINUTES);
            charts.push(buildChartForMetric(AGENT_TX_TRANSFERRED_BYTES, baseChartOptions, agent));
            metrics.push(AGENT_TX_TRANSFERRED_BYTES);
            charts.push(buildChartForMetric(AGENT_RX_TRANSFERRED_BYTES, baseChartOptions, agent));
            metrics.push(AGENT_RX_TRANSFERRED_BYTES);
        }
    }

    return { charts, metrics };
}

function buildChartForMetric(metricName, baseChartOptions, agent) {
    const metric = {
        name: metricName,
        title: baseChartOptions[metricName].title,
        formatter: baseChartOptions[metricName].formatter,
        color: baseChartOptions[metricName].chartLineColor,
        showInLegend: true,
        units: baseChartOptions[metricName].units,
        multiplier: 0,
    };

    return buildChartDefinitionObject(baseChartOptions[metricName].title, metric, agent, baseChartOptions, {});
}

function buildChartDefinitionObject(chartTitle, metric, agent, baseChartOptions, options) {
    return {
        cardTitle: chartTitle,
        metricName: metric.name,
        config: buildChartConfig(agent, metric, baseChartOptions, options),
    };
}

function buildChartConfig(agent, metric, baseChartOptions, options = {}) {
    const chartConfig = buildBaseTestChartConfig(metric, baseChartOptions);
    chartConfig.chartTrends = buildTrendFromMetric(metric, agent, options);

    return chartConfig;
}

function buildBaseTestChartConfig(metric, baseChartOptions) {
    return _.extend(buildYAxisObject(metric.name, baseChartOptions), {
        title: {
            text: '',
        },
        chart: {
            height: 150,
            zoomType: '',
        },
        chartType: 'line',
        tooltip: BuiltInTooltip,
        xAxis: {},
        legend: {
            enabled: true,
        },
    });
}

function buildTrendFromMetric(metric) {
    const { name: key, title, color, formatter, units, multiplier } = metric;

    const trend = _.extend(
        {},
        {
            formatter: formatter || FORMATTER_TYPES.PERCENT,
            forceDecimals: true,
            units,
            chartType: 'line',
            key,
            label: title,
            color,
            multiplier,
        }
    );

    return [trend];
}

function buildYAxisTitle(title, units) {
    return !_.isEmpty(units) ? `${title} (${units})` : title;
}

function buildYAxisObject(metricName, baseChartOptions) {
    const baseOptions = baseChartOptions[metricName];
    const yAxisOptions = {
        title: {
            text: buildYAxisTitle(baseOptions.yTitle, baseOptions.units),
        },
    };

    yAxisOptions.min = !baseOptions.yMin ? baseOptions.yMin : 0;

    if (baseOptions.yMax) {
        // If endOnTick isn't set to false, it ignores the max
        yAxisOptions.endOnTick = false;
        yAxisOptions.softMax = baseOptions.yMax;
    }

    return {
        yAxis: [yAxisOptions],
    };
}

export function getTrendsTimeRange(rangeInMinutes) {
    const end = getSystemTime();
    // Subtract the last-n duration from the end time to arrive at the start time.
    const start = moment(end).subtract(rangeInMinutes, 'minutes');

    // Return the aligned UTC range for the query.
    return {
        start: start.valueOf(),
        end: end.valueOf(),
    };
}

export function useVitalsChartData(agent, trends, defaultTimeRange) {
    const apiUrl = `/query/chart`;
    const dataService = useDataService();
    const [chartData, setChartData] = useState(null);
    const handleGenericError = useGenericError();
    const intervalHandle = useRef(null);

    const refreshChartData = async (timeRange) => {
        if (intervalHandle.current) {
            clearInterval(intervalHandle.current);
        }

        intervalHandle.current = setInterval(() => loadChartData(timeRange), DEFAULT_HEALTH_REFRESH_INTERVAL);
        return loadChartData(timeRange);
    };

    const loadChartData = async (timeRange) => {
        try {
            const timeRangeParams = getTrendsTimeRange(timeRange);
            const promises = _.map(trends, (trend) => loadChart(_.extend({ metrics: trend }, timeRangeParams)));
            const results = await Promise.all(promises);
            const resultMap = _.reduce(
                trends,
                (chartObject, trendMetric, index) => {
                    return _.merge(chartObject, results[index].data);
                },
                {}
            );
            const newChartData = addGapsToTrendData(resultMap[0]);
            setChartData(newChartData);
        } catch (e) {
            handleGenericError(e);
        }
    };

    const loadChart = async (metricParams) => {
        const agentType = 'agent';
        const queryParams = _.extend(metricParams, {
            agent: agent._id,
            type: agentType,
        });

        return dataService.get(apiUrl, queryParams);
    };

    useEffect(() => {
        refreshChartData(defaultTimeRange);
        return () => clearInterval(intervalHandle.current);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return { chartData, refreshChartData };
}

function addGapsToTrendData(chartData) {
    return {
        ...chartData,
        trends: mapValues(chartData.trends, (trend) => {
            return {
                ...trend,
                data: flatMap(trend.data, (point, i, trendData) => {
                    const lastPoint = trendData[i - 1];
                    // We don't care about slight delays at the minute level so add a bit of wiggle room
                    const gapSizeMultiplier = trend.granularity === ONE_MIN_AGGREGATION_VALUE ? 2 : 1;
                    return i !== 0 && point.date - lastPoint.date > trend.granularity * gapSizeMultiplier
                        ? [{ date: lastPoint.date + 1, value: null, count: 1 }, point]
                        : point;
                }),
            };
        }),
    };
}
